import { createSelector } from "reselect";

import { IntentResource } from "types/generated";

import { isModuleResourcesGroup } from "../helpers";
import { IntentProjectResourcesHierarchyStore } from "./useResourcesHierarchyStore";

export enum ResourcesServiceRowType {
  LoadMore = "load-more",
}

export type ResourcesServiceRow = {
  id: string;
  serviceType: ResourcesServiceRowType;
  groupId: string;
  intentProjectId: string;
};

export type IntentResourceRow = IntentResource & {
  isChild: boolean;
  isGroup: boolean;
};

type ResourcesTableRow = IntentResourceRow | ResourcesServiceRow;

type FlatListState = Pick<IntentProjectResourcesHierarchyStore, "childrenByGroup" | "groupMeta">;

export const selectFlatList = createSelector(
  (state: FlatListState) => state.childrenByGroup,
  (state: FlatListState) => state.groupMeta,
  (_state: FlatListState, parentResources: IntentResource[]) => parentResources,
  (
    childrenByGroup,
    groupMeta,
    parentResources
  ): { rows: ResourcesTableRow[]; hasGroups: boolean; stickyRanges: [number, number][] } => {
    const rows: ResourcesTableRow[] = [];
    const stickyRanges: [number, number][] = [];
    let hasGroups = false;

    for (const resource of parentResources) {
      const meta = groupMeta[resource.resourceID];
      const isModule = isModuleResourcesGroup(resource.resourceType);

      if (isModule) {
        hasGroups = true;
      }

      const parentIndex = rows.length;
      rows.push({ ...resource, isChild: false, isGroup: isModule });

      if (!meta?.isOpen) {
        continue;
      }

      const children = childrenByGroup[resource.resourceID] ?? [];

      for (const child of children) {
        rows.push({
          ...child,
          isChild: true,
          isGroup: isModuleResourcesGroup(child.resourceType),
        });
      }

      if (meta.hasMore) {
        rows.push({
          id: `${resource.resourceID}-load-more`,
          serviceType: ResourcesServiceRowType.LoadMore,
          groupId: resource.resourceID,
          intentProjectId: resource.intentProjectID,
        });
      }

      // Keep the module header pinned until its whole group scrolls past, the range covers
      // every child plus the trailing load-more row, so the header doesn't detach early.
      if (isModule && children.length > 0) {
        stickyRanges.push([parentIndex, rows.length - 1]);
      }
    }

    return { rows, hasGroups, stickyRanges };
  }
);
